/** Маппинг DeviceOrientation → yaw/pitch с учётом портрета и ландшафта. Без позиции в пространстве. */

export type DeviceOrientationPermission = "granted" | "denied" | "unsupported"

/** pitchOnly: stick yaw + phone pitch. full: phone yaw+pitch (legacy). */
export type DeviceLookMode = "pitchOnly" | "full"

export type DeviceAngles = {
  alpha: number
  beta: number
  /** Событие без gamma пропускается. Старые вызовы без поля читаются как 0. */
  gamma?: number
}

export type LookAngles = {
  yaw: number
  pitch: number
}

export type DeviceOrientationLookHandle = {
  calibrate: (cameraLook: LookAngles, device?: DeviceAngles | null) => void
  stop: () => void
}

/** База взгляда между сэмплами. Смена угла экрана переписывает её, не сдвигая камеру. */
export type DeviceLookTrack = {
  cameraBase: LookAngles
  deviceBase: DeviceAngles | null
  screenDeg: number
  emittedLook: LookAngles | null
}

type DeviceOrientationPermissionFn = () => Promise<"granted" | "denied" | "default">

type DeviceOrientationEventCtor = {
  requestPermission?: DeviceOrientationPermissionFn
}

const DEG2RAD = Math.PI / 180
/** Уже device look: уже мышиного PITCH_MAX, чтобы не смотреть в void. */
export const DEVICE_LOOK_PITCH_MAX = 1.0
/** Ниже этой длины горизонтальной проекции взгляд почти вертикальный: курс берём у верха экрана. */
const VERTICAL_LOOK_EPS = 0.2
/** Скачок beta/alpha при init сенсора — перебазировать, не крутить камеру в небо. */
export const DEVICE_LOOK_REBASE_DELTA_DEG = 35
/** Portrait: телефон «вертикально» ≈ beta 90°. Тот же ноль у верха картинки в ландшафте. */
export const DEVICE_LOOK_UPRIGHT_BETA_DEG = 90
/** Поворот влево-вправо с телефона. 2 — тот же наклон даёт вдвое больший yaw. */
export const DEVICE_LOOK_YAW_GAIN = 2
/** Наклон вверх-вниз с телефона. 1 — градус телефона равен градусу камеры. */
export const DEVICE_LOOK_PITCH_GAIN = 1

function clampPitch(pitch: number) {
  return Math.min(DEVICE_LOOK_PITCH_MAX, Math.max(-DEVICE_LOOK_PITCH_MAX, pitch))
}

/** Нормализация дельты градусов в (−180, 180]. */
export function shortestAngleDeltaDeg(from: number, to: number): number {
  let d = to - from
  d = ((((d + 180) % 360) + 360) % 360) - 180
  return d
}

/** 0 / 90 / 180 / 270. −90 (iOS) → 270. */
export function normalizeScreenAngleDeg(deg: number): number {
  const wrapped = ((deg % 360) + 360) % 360
  return (Math.round(wrapped / 90) * 90) % 360
}

type Vec3 = [number, number, number]
type Mat3 = [Vec3, Vec3, Vec3]

/** R = Rz(−α) Rx(−β) Ry(−γ), как в спецификации DeviceOrientation. */
function orientationMatrix(device: DeviceAngles): Mat3 {
  const alpha = device.alpha * DEG2RAD
  const beta = device.beta * DEG2RAD
  const gamma = (device.gamma ?? 0) * DEG2RAD
  const cX = Math.cos(beta)
  const cY = Math.cos(gamma)
  const cZ = Math.cos(alpha)
  const sX = Math.sin(beta)
  const sY = Math.sin(gamma)
  const sZ = Math.sin(alpha)
  return [
    [cZ * cY - sZ * sX * sY, -cX * sZ, cZ * sY + cY * sZ * sX],
    [cY * sZ + cZ * sX * sY, cZ * cX, sZ * sY - cZ * cY * sX],
    [-cX * sY, sX, cX * cY],
  ]
}

function matMul(a: Mat3, b: Mat3): Mat3 {
  const row = (i: number): Vec3 => [
    a[i][0] * b[0][0] + a[i][1] * b[1][0] + a[i][2] * b[2][0],
    a[i][0] * b[0][1] + a[i][1] * b[1][1] + a[i][2] * b[2][1],
    a[i][0] * b[0][2] + a[i][1] * b[1][2] + a[i][2] * b[2][2],
  ]
  return [row(0), row(1), row(2)]
}

/** Поворот осей корпуса на угол экрана. 90°: верх картинки = +X корпуса. */
function screenRotation(screenDeg: number): Mat3 {
  const rad = -normalizeScreenAngleDeg(screenDeg) * DEG2RAD
  const c = Math.cos(rad)
  const s = Math.sin(rad)
  return [
    [c, -s, 0],
    [s, c, 0],
    [0, 0, 1],
  ]
}

/**
 * Направление «в экран». iOS за горизонтом отдаёт другую тройку углов того же поворота:
 * alpha сдвинут, gamma около ±180, beta идёт обратно. Матрица у обеих троек одна.
 * Курс почти вертикального взгляда берём у верха экрана, иначе поворот лёжа на столе теряется.
 * Раньше переключаться нельзя: за горизонтом верх экрана смотрит назад, а взгляд — ещё вперёд.
 */
function deviceDirection(device: DeviceAngles, screenDeg: number): LookAngles {
  const m = matMul(orientationMatrix(device), screenRotation(screenDeg))
  const look: Vec3 = [-m[0][2], -m[1][2], -m[2][2]]
  const up: Vec3 = [m[0][1], m[1][1], m[2][1]]
  const pitch = Math.asin(Math.min(1, Math.max(-1, look[2])))
  const heading = (v: Vec3) => -Math.atan2(-v[0], -v[1])
  const yaw = Math.hypot(look[0], look[1]) >= VERTICAL_LOOK_EPS ? heading(look) : heading(up)
  return { yaw, pitch }
}

function directionDeltaDeg(from: DeviceAngles, to: DeviceAngles, screenDeg: number, mode: DeviceLookMode) {
  const base = deviceDirection(from, screenDeg)
  const now = deviceDirection(to, screenDeg)
  const dPitch = Math.abs((now.pitch - base.pitch) / DEG2RAD)
  const dYaw = mode === "full" ? Math.abs(shortestAngleDeltaDeg(base.yaw / DEG2RAD, now.yaw / DEG2RAD)) : 0
  return { dPitch, dYaw }
}

/**
 * Относительный взгляд после калибровки.
 * full: курс направления → yaw, высота направления → pitch.
 * pitchOnly: поворот телефона не берём, только высота.
 */
export function lookFromDeviceDelta(
  cameraBase: LookAngles,
  deviceBase: DeviceAngles,
  deviceNow: DeviceAngles,
  mode: DeviceLookMode = "full",
  screenDeg = 0,
): LookAngles {
  const base = deviceDirection(deviceBase, screenDeg)
  const now = deviceDirection(deviceNow, screenDeg)
  const pitch = clampPitch(cameraBase.pitch + (now.pitch - base.pitch) * DEVICE_LOOK_PITCH_GAIN)
  if (mode === "pitchOnly") return { yaw: cameraBase.yaw, pitch }
  const dYawDeg = shortestAngleDeltaDeg(base.yaw / DEG2RAD, now.yaw / DEG2RAD)
  return {
    yaw: cameraBase.yaw + dYawDeg * DEG2RAD * DEVICE_LOOK_YAW_GAIN,
    pitch,
  }
}

/**
 * Один сэмпл. look = null — базу только запомнили.
 * Смена угла экрана возвращает последний взгляд и ставит новую базу, без скачка.
 */
export function reduceDeviceLook(
  track: DeviceLookTrack,
  device: DeviceAngles,
  screenDeg: number,
  mode: DeviceLookMode,
  cameraNow: LookAngles,
): { track: DeviceLookTrack; look: LookAngles | null } {
  const screen = normalizeScreenAngleDeg(screenDeg)
  if (!track.deviceBase) {
    return {
      track: { cameraBase: cameraNow, deviceBase: device, screenDeg: screen, emittedLook: null },
      look: null,
    }
  }
  if (screen !== track.screenDeg) {
    const cameraBase = track.emittedLook ?? cameraNow
    return {
      track: { cameraBase, deviceBase: device, screenDeg: screen, emittedLook: track.emittedLook },
      look: track.emittedLook,
    }
  }
  if (!track.emittedLook) {
    const delta = directionDeltaDeg(track.deviceBase, device, screen, mode)
    // iOS/Android: «нулевой» sample → реальный наклон — не уводить pitch в потолок.
    // Смена ветки углов за горизонтом здесь почти 0, её не перебазируем.
    if (delta.dPitch > DEVICE_LOOK_REBASE_DELTA_DEG || delta.dYaw > DEVICE_LOOK_REBASE_DELTA_DEG) {
      return {
        track: { cameraBase: cameraNow, deviceBase: device, screenDeg: screen, emittedLook: null },
        look: null,
      }
    }
  }
  const look = lookFromDeviceDelta(track.cameraBase, track.deviceBase, device, mode, screen)
  return {
    track: { ...track, screenDeg: screen, emittedLook: look },
    look,
  }
}

export function isDeviceOrientationSupported(): boolean {
  return typeof window !== "undefined" && "DeviceOrientationEvent" in window
}

export async function requestDeviceOrientationPermission(): Promise<DeviceOrientationPermission> {
  if (!isDeviceOrientationSupported()) return "unsupported"

  const ctor = DeviceOrientationEvent as unknown as DeviceOrientationEventCtor
  if (typeof ctor.requestPermission === "function") {
    try {
      const result = await ctor.requestPermission()
      return result === "granted" ? "granted" : "denied"
    } catch {
      return "denied"
    }
  }

  return "granted"
}

/** `screen.orientation.angle`, иначе `window.orientation`. Вне браузера — 0. */
export function readScreenAngleDeg(): number {
  if (typeof window === "undefined") return 0
  const screenAngle = window.screen?.orientation?.angle
  if (typeof screenAngle === "number" && Number.isFinite(screenAngle)) return normalizeScreenAngleDeg(screenAngle)
  const legacy = (window as Window & { orientation?: number }).orientation
  if (typeof legacy === "number" && Number.isFinite(legacy)) return normalizeScreenAngleDeg(legacy)
  return 0
}

function readDeviceAngles(ev: DeviceOrientationEvent): DeviceAngles | null {
  if (ev.alpha == null || ev.beta == null || ev.gamma == null) return null
  if (!Number.isFinite(ev.alpha) || !Number.isFinite(ev.beta) || !Number.isFinite(ev.gamma)) return null
  return { alpha: ev.alpha, beta: ev.beta, gamma: ev.gamma }
}

export type StartDeviceOrientationLookOptions = {
  onLook: (look: LookAngles) => void
  getCameraLook: () => LookAngles
  /** Current look mode; read each event so toggle can change without resubscribe. */
  getMode?: () => DeviceLookMode
  /** Для тестов: сразу задать базу без ожидания первого события. */
  initialDevice?: DeviceAngles | null
  /** Fired once when the first valid orientation sample arrives. */
  onFirstSample?: () => void
}

export function startDeviceOrientationLook(opts: StartDeviceOrientationLookOptions): DeviceOrientationLookHandle {
  const { onLook, getCameraLook, getMode, onFirstSample } = opts

  let lastDevice: DeviceAngles | null = opts.initialDevice ?? null
  let track: DeviceLookTrack = {
    cameraBase: getCameraLook(),
    deviceBase: opts.initialDevice ?? null,
    screenDeg: readScreenAngleDeg(),
    emittedLook: null,
  }
  let stopped = false
  let firstSampleSent = opts.initialDevice != null

  const noteFirstSample = () => {
    if (firstSampleSent) return
    firstSampleSent = true
    onFirstSample?.()
  }

  const apply = (device: DeviceAngles) => {
    lastDevice = device
    noteFirstSample()
    const mode = getMode?.() ?? "full"
    const next = reduceDeviceLook(track, device, readScreenAngleDeg(), mode, getCameraLook())
    track = next.track
    if (next.look) onLook(next.look)
  }

  const onOrientation = (ev: Event) => {
    if (stopped) return
    const angles = readDeviceAngles(ev as DeviceOrientationEvent)
    if (!angles) return
    apply(angles)
  }

  const absoluteSupported = typeof window !== "undefined" && "ondeviceorientationabsolute" in window
  const isAppleTouch = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent)
  // Absolute на части Android ок; на iOS только relative.
  const orientationEvent: "deviceorientationabsolute" | "deviceorientation" =
    absoluteSupported && !isAppleTouch ? "deviceorientationabsolute" : "deviceorientation"

  window.addEventListener(orientationEvent, onOrientation)

  return {
    calibrate(cameraLook: LookAngles, device?: DeviceAngles | null) {
      track = { ...track, cameraBase: cameraLook, deviceBase: device ?? lastDevice }
    },
    stop() {
      if (stopped) return
      stopped = true
      window.removeEventListener(orientationEvent, onOrientation)
    },
  }
}
